import React from 'react';
import { Youtube, Facebook, Instagram, Linkedin, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { PLATFORM_COLORS, PLATFORMS } from '../../../utils/socialMediaHelpers';

const PLATFORM_ICONS = {
    youtube: Youtube,
    facebook: Facebook,
    instagram: Instagram,
    linkedin: Linkedin,
};

const PlatformSelector = ({
    integrations = [],
    selectedIntegrationIds = [],
    onSelectionChange,
    disabled = false
}) => {
    const toggleIntegration = (integrationId) => {
        if (disabled) return;

        const isSelected = selectedIntegrationIds.includes(integrationId);
        if (isSelected) {
            onSelectionChange(selectedIntegrationIds.filter(id => id !== integrationId));
        } else {
            onSelectionChange([...selectedIntegrationIds, integrationId]);
        }
    };

    if (integrations.length === 0) {
        return (
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-center">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                    No connected accounts. Connect an account to start posting.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Post to
            </label>
            <div className="flex flex-wrap gap-3">
                {integrations.map((integration) => {
                    const Icon = PLATFORM_ICONS[integration.platform] || Youtube;
                    const colors = PLATFORM_COLORS[integration.platform];
                    const isSelected = selectedIntegrationIds.includes(integration.id);
                    const platformInfo = PLATFORMS[integration.platform];

                    return (
                        <motion.button
                            key={integration.id}
                            type="button"
                            onClick={() => toggleIntegration(integration.id)}
                            disabled={disabled || integration.status === 'expired'}
                            whileHover={{ scale: disabled ? 1 : 1.02 }}
                            whileTap={{ scale: disabled ? 1 : 0.98 }}
                            className={`relative flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all ${
                                isSelected
                                    ? `${colors.border} ${colors.dark.border} ${colors.bg} ${colors.dark.bg}`
                                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
                            } ${
                                disabled || integration.status === 'expired'
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'cursor-pointer'
                            }`}
                        >
                            {/* Avatar with platform icon overlay */}
                            <div className="relative">
                                {integration.avatar_url ? (
                                    <img
                                        src={integration.avatar_url}
                                        alt={integration.account_name}
                                        className={`w-10 h-10 rounded-full border-2 ${
                                            isSelected
                                                ? 'border-white dark:border-slate-700'
                                                : 'border-slate-200 dark:border-slate-600'
                                        } ${!isSelected ? 'grayscale' : ''}`}
                                    />
                                ) : (
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colors.bg} ${colors.dark.bg} ${!isSelected ? 'grayscale' : ''}`}>
                                        <Icon className={`w-5 h-5 ${colors.text} ${colors.dark.text}`} />
                                    </div>
                                )}
                                {/* Platform icon badge */}
                                <div className={`absolute -bottom-1 -right-1 p-1 rounded-full ${colors.bg} ${colors.dark.bg} border border-white dark:border-slate-700`}>
                                    <Icon className={`w-3 h-3 ${colors.text} ${colors.dark.text}`} />
                                </div>
                            </div>

                            {/* Account info */}
                            <div className="text-left min-w-0">
                                <p className={`text-sm font-medium truncate ${
                                    isSelected
                                        ? 'text-slate-900 dark:text-white'
                                        : 'text-slate-700 dark:text-slate-300'
                                }`}>
                                    {integration.display_name || integration.account_name}
                                </p>
                                <p className={`text-xs truncate ${
                                    isSelected
                                        ? 'text-slate-600 dark:text-slate-400'
                                        : 'text-slate-500 dark:text-slate-500'
                                }`}>
                                    {platformInfo?.name || integration.platform}
                                </p>
                            </div>

                            {/* Selection indicator */}
                            {isSelected && (
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className={`absolute top-2 right-2 p-1 rounded-full ${colors.bg} ${colors.dark.bg}`}
                                >
                                    <Check className={`w-3 h-3 ${colors.text} ${colors.dark.text}`} />
                                </motion.div>
                            )}

                            {/* Expired indicator */}
                            {integration.status === 'expired' && (
                                <span className="absolute top-2 right-2 text-xs font-medium text-yellow-600 dark:text-yellow-400">
                                    Reconnect
                                </span>
                            )}
                        </motion.button>
                    );
                })}
            </div>
        </div>
    );
};

export default PlatformSelector;
